import React, { useState } from 'react'
import { File, X } from 'lucide-react'
import { Button, Loader } from 'components/ui'
import LocaleText from 'components/LocaleText'
import useLocaleText from 'components/useLocaleText'
import { getFileKind } from 'components/Library/helpers'
import { DriveNodeType } from 'config/driveTypes'
import { MediaUploadPreview } from '../MediaUploadPreview'
import { classNames } from '../classNames'
import { AttachmentData, formatFileSize, safeMediaUrl, isImageAttachment } from './model'
import { attachmentText } from './literals'
import styles from './styles.module.css'

export const Attachment: React.FC<{
  attachment: AttachmentData
  download?: boolean
  uploading?: boolean
  error?: boolean
  removeDisabled?: boolean
  onRemove?: () => void
  onPreview?: (url: string, name: string) => void
}> = ({ attachment, uploading, error, onRemove, removeDisabled, onPreview, download }) => {
  const [failedPreview, setFailedPreview] = useState<string>()

  const t = useLocaleText(attachmentText)
  const extension = attachment.name.split('.').pop()?.toUpperCase()
  const url = safeMediaUrl(attachment.url)
  const photo = isImageAttachment(attachment)
  const video = /^video\//.test(attachment.mime || '') || /\.(mp4|mov|webm|m4v|avi|mkv)$/i.test(attachment.name)
  const previewImage = photo && url && failedPreview !== url
  const previewVideo = video && url && failedPreview !== url

  const mime =
    attachment.mime ||
    (extension === 'PDF'
      ? 'application/pdf'
      : ['XLS', 'XLSX', 'CSV'].includes(extension || '')
      ? 'application/vnd.ms-excel'
      : '')
  const kind = photo ? 'image' : getFileKind({ type: DriveNodeType.FILE, mime_type: mime })
  const fileBadge = ['XLS', 'XLSX', 'CSV', 'PDF'].includes(extension || '') ? extension : <File size={20} />

  if (onRemove && (previewImage || previewVideo))
    return (
      <MediaUploadPreview
        src={url}
        href={onPreview ? undefined : url}
        mediaType={previewVideo ? 'video' : 'image'}
        alt={attachment.name}
        previewLabel={t.preview.replace('{name}', attachment.name)}
        uploadingLabel={t.uploading.replace('{name}', attachment.name)}
        uploading={uploading}
        error={error}
        errorLabel={t.failed}
        onPreview={onPreview ? () => onPreview(url!, attachment.name) : undefined}
        onMediaError={() => setFailedPreview(url)}
        title={`${attachment.name}${attachment.size == null ? '' : ` · ${formatFileSize(attachment.size)}`}`}
        removeAction={{
          label: t.remove.replace('{name}', attachment.name),
          onClick: onRemove,
          disabled: removeDisabled,
        }}
      />
    )

  if (onRemove)
    return (
      <div
        className={classNames(styles.file, styles.attachmentDraft)}
        title={`${attachment.name}${attachment.size == null ? '' : ` · ${formatFileSize(attachment.size)}`}`}
        aria-busy={!!uploading && !error}
      >
        <Button
          as={url ? 'a' : 'button'}
          href={url}
          target={url ? '_blank' : undefined}
          rel={url ? 'noopener noreferrer' : undefined}
          isDisabled={!url}
          variant="ghost"
          aria-label={t.preview.replace('{name}', attachment.name)}
          className={styles.attachmentPreview}
          _hover={{ bg: '#FAFAF8' }}
          _focus={{ boxShadow: 'inset 0 0 0 2px #8C8C84', outline: 'none' }}
        >
          {uploading && !error ? (
            <span
              className={styles.attachmentLoading}
              role="status"
              aria-label={t.uploading.replace('{name}', attachment.name)}
            >
              <Loader size="md" color="#A3A3A3" h="40px" />
            </span>
          ) : (
            <span className={styles.fileBadge}>{fileBadge}</span>
          )}
          <span className={styles.attachmentCopy}>
            <span className={styles.fileName}>
              <bdi>{attachment.name}</bdi>
            </span>
            <span className={styles.fileMeta}>
              {error ? <span className={styles.attachmentError}>{t.failed}</span> : <LocaleText text={kind} />}
              {attachment.size != null && ` · ${formatFileSize(attachment.size)}`}
            </span>
          </span>
        </Button>
        <Button
          variant="icon"
          className={styles.attachmentRemove}
          aria-label={t.remove.replace('{name}', attachment.name)}
          onClick={onRemove}
          isDisabled={removeDisabled}
          color="#8C8C84"
          _hover={{ bg: '#F1F1ED', color: '#1E1E1E' }}
          p={0}
          minW="28px"
          w="28px"
          h="28px"
        >
          <X size={15} strokeWidth={1.8} />
        </Button>
      </div>
    )

  const cardLink = url && !download ? url : undefined
  const Card = cardLink ? 'a' : 'div'

  return (
    <Card
      className={classNames(styles.file, cardLink && styles.fileLink, !url && styles.fileUnavailable)}
      href={cardLink}
      target={cardLink ? '_blank' : undefined}
      rel={cardLink ? 'noopener noreferrer' : undefined}
    >
      <span className={styles.fileBadge}>{fileBadge}</span>
      <div className={styles.fileContent}>
        <div className={styles.fileName} dir="auto">
          {attachment.name}
        </div>
        <div className={styles.fileMeta}>
          {!url ? t.unavailable : <LocaleText text={kind} />}
          {attachment.size != null && ` · ${formatFileSize(attachment.size)}`}
        </div>
      </div>
      {url && download && (
        <Button
          as="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          download={download || undefined}
          variant="secondary"
        >
          {t.download}
        </Button>
      )}
    </Card>
  )
}
